import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PrivateRoute from 'Components/Route/PrivateRoute';
import TodaysRecipe from 'Components/TodaysRecipe/TodaysRecipe';
import { useAuth } from 'Hooks/useAuth';
import { useNotification } from 'Hooks/useNotification';
import { useResponsive } from 'Hooks/useResponsive';
import ButtonAppBar from 'Layouts/ButtonAppBar/ButtonAppBar';
import { recipeService } from 'Services/recipeService';
import { Recipe, RecipeStatus } from 'Types/api';
import { parseRecipeFile } from 'Utils/exportUtils';
import { ROUTES } from 'Utils/routes';

const Dashboard = () => {
  const { isLoading: authLoading } = useAuth();
  const { showNotification } = useNotification();
  const { isMobile } = useResponsive();
  const navigate = useNavigate();

  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<RecipeStatus | ''>('');
  const fetchedRef = useRef(false);
  const importInputRef = useRef<HTMLInputElement>(null);

  const categories = useMemo(() => {
    const cats = new Set(recipes.map((r) => r.category));
    return Array.from(cats).sort();
  }, [recipes]);

  const filteredRecipes = useMemo(() => {
    return recipes.filter((recipe) => {
      const matchesSearch =
        !searchQuery ||
        recipe.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        recipe.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = !selectedCategory || recipe.category === selectedCategory;
      const matchesStatus = !selectedStatus || recipe.status === selectedStatus;
      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [recipes, searchQuery, selectedCategory, selectedStatus]);

  const fetchRecipes = useCallback(async (token?: string) => {
    try {
      const response = await recipeService.list(token);
      setRecipes((prev) => (token ? [...prev, ...response.items] : response.items));
      setNextToken(response.nextToken);
      setError(null);
    } catch (err) {
      setError('Failed to load recipes. Please try again.');
    }
  }, []);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchRecipes().finally(() => setIsLoading(false));
  }, [fetchRecipes]);

  const handleLoadMore = async () => {
    setIsLoadingMore(true);
    await fetchRecipes(nextToken);
    setIsLoadingMore(false);
  };

  const handleImportClick = () => {
    importInputRef.current?.click();
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so same file can be selected again
    if (importInputRef.current) {
      importInputRef.current.value = '';
    }

    setIsImporting(true);
    try {
      const result = await parseRecipeFile(file);

      if (!result.success || !result.data) {
        const errorMessages = result.errors?.map((e) => e.message).join(', ') || 'Invalid file';
        showNotification(`Import failed: ${errorMessages}`);
        return;
      }

      // Create the recipe via API
      const newRecipe = await recipeService.create(result.data);
      showNotification(`Recipe "${newRecipe.title}" imported successfully!`, 'success');

      // Navigate to the new recipe
      navigate(`/recipes/${newRecipe.id}`);
    } catch {
      showNotification('Failed to import recipe. Please try again.');
    } finally {
      setIsImporting(false);
    }
  };

  if (authLoading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress color='secondary' />
      </Box>
    );
  }

  return (
    <PrivateRoute>
      <ButtonAppBar />
      <Container maxWidth='lg'>
        <Box component='main' sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2 } }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant={isMobile ? 'h5' : 'h4'} component='h1'>
              My Recipes
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <input
                ref={importInputRef}
                type='file'
                accept='.json,application/json'
                style={{ display: 'none' }}
                onChange={handleImportFile}
              />
              <Button
                variant='outlined'
                startIcon={isImporting ? <CircularProgress size={16} /> : <UploadFileIcon />}
                onClick={handleImportClick}
                disabled={isImporting}
              >
                {isMobile ? 'Import' : 'Import Recipe'}
              </Button>
              <Button
                variant='contained'
                color='secondary'
                startIcon={<AddIcon />}
                onClick={() => navigate(ROUTES.CREATE_RECIPE)}
              >
                {isMobile ? 'New' : 'New Recipe'}
              </Button>
            </Box>
          </Box>

          <TodaysRecipe recipes={recipes} isLoading={isLoading} />

          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <TextField
              size='small'
              placeholder='Search recipes...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ flexGrow: 1, minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon color='action' />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position='end'>
                    <ClearIcon
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setSearchQuery('')}
                      fontSize='small'
                    />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size='small' sx={{ minWidth: 150 }}>
              <InputLabel id='category-filter-label'>Category</InputLabel>
              <Select
                labelId='category-filter-label'
                id='category-filter'
                value={selectedCategory}
                label='Category'
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <MenuItem value=''>All Categories</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size='small' sx={{ minWidth: 120 }}>
              <InputLabel id='status-filter-label'>Status</InputLabel>
              <Select
                labelId='status-filter-label'
                id='status-filter'
                value={selectedStatus}
                label='Status'
                onChange={(e) => setSelectedStatus(e.target.value as RecipeStatus | '')}
              >
                <MenuItem value=''>All</MenuItem>
                <MenuItem value='published'>Published</MenuItem>
                <MenuItem value='draft'>Draft</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isLoading ? (
            <RecipeListSkeleton />
          ) : recipes.length === 0 ? (
            <EmptyState />
          ) : filteredRecipes.length === 0 ? (
            <NoResults
              onClear={() => {
                setSearchQuery('');
                setSelectedCategory('');
                setSelectedStatus('');
              }}
            />
          ) : (
            <>
              <Grid container spacing={2}>
                {filteredRecipes.map((recipe) => (
                  <Grid item xs={12} sm={6} md={4} key={recipe.id}>
                    <RecipeCard recipe={recipe} />
                  </Grid>
                ))}
              </Grid>

              {nextToken && !searchQuery && !selectedCategory && !selectedStatus && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Button
                    variant='outlined'
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                    startIcon={isLoadingMore ? <CircularProgress size={16} /> : undefined}
                  >
                    {isLoadingMore ? 'Loading...' : 'Load More'}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Container>
    </PrivateRoute>
  );
};

const RecipeCard = ({ recipe }: { recipe: Recipe }) => {
  const navigate = useNavigate();
  const totalTime = recipe.prepTime + recipe.cookTime;

  return (
    <Card variant='outlined' sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardActionArea onClick={() => navigate(`/recipes/${recipe.id}`)} sx={{ flexGrow: 1 }}>
        {recipe.imageUrl ? (
          <CardMedia component='img' height='140' image={recipe.imageUrl} alt={recipe.title} />
        ) : (
          <Box
            sx={{
              height: 140,
              bgcolor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <RestaurantIcon sx={{ fontSize: 56, color: 'grey.400' }} />
          </Box>
        )}
        <CardContent>
          <Typography variant='h6' component='h2' gutterBottom noWrap>
            {recipe.title}
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{
              mb: 2,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {recipe.description}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {recipe.status === 'draft' && <Chip label='Draft' size='small' color='warning' />}
            <Chip
              icon={<AccessTimeIcon />}
              label={`${totalTime} min`}
              size='small'
              variant='outlined'
            />
            <Chip
              icon={<RestaurantIcon />}
              label={`${recipe.servings} servings`}
              size='small'
              variant='outlined'
            />
            <Chip label={recipe.category} size='small' color='primary' variant='outlined' />
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const RecipeListSkeleton = () => (
  <Grid container spacing={2}>
    {[1, 2, 3, 4, 5, 6].map((i) => (
      <Grid item xs={12} sm={6} md={4} key={i}>
        <Card variant='outlined'>
          <CardContent>
            <Skeleton variant='text' width='80%' height={32} />
            <Skeleton variant='text' width='100%' />
            <Skeleton variant='text' width='60%' />
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Skeleton variant='rounded' width={80} height={24} />
              <Skeleton variant='rounded' width={90} height={24} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

const EmptyState = () => (
  <Box sx={{ textAlign: 'center', py: 6 }}>
    <RestaurantIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
    <Typography variant='h6' color='text.secondary' gutterBottom>
      No recipes yet
    </Typography>
    <Typography variant='body2' color='text.secondary'>
      Your recipes will appear here once you create them.
    </Typography>
  </Box>
);

const NoResults = ({ onClear }: { onClear: () => void }) => (
  <Box sx={{ textAlign: 'center', py: 6 }}>
    <SearchIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
    <Typography variant='h6' color='text.secondary' gutterBottom>
      No recipes found
    </Typography>
    <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
      Try adjusting your search or filter.
    </Typography>
    <Button variant='outlined' size='small' onClick={onClear}>
      Clear Filters
    </Button>
  </Box>
);

export default Dashboard;
